/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { ReactNode, useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import FileUploader from '../FileUploader'

const validationSchema = yup.object().shape({
  name: yup.string().required('O nome é obrigatório').default('').min(0).max(255),
  placeholder: yup.string().default('').min(0).max(255).nullable(),
  url: yup.string().default('').min(0).max(255).nullable(),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('inactive'),
  image: yup.lazy(value => typeof value === 'object'
    ? yup.object().shape({ url: yup.string(), mimetype: yup.string() }).nullable()
    : yup.string().nullable()
  )
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormBrandHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormBrandHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormBrand is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormBrand from './src/views/PostEdit/FormBrand.tsx'
 *
 * const MyComponent = () => (
 *   <FormBrand />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormBrand: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Nome da marca (vindo do CRM)">
            <Form.Control
              type='text'
              placeholder='Informe o nome da marca'
              name='name'
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Apelido (será utilizado no site)">
            <Form.Control
              type='text'
              name='placeholder'
              value={formik.values.placeholder ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
          <Form.Text className="text-muted">
            Este campo é utilizado para exibir a marca no site, como por exemplo: "Marca X".
          </Form.Text>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="URL da marca">
            <Form.Control
              type='text'
              placeholder='Informe o link da marca'
              name='url'
              value={formik.values?.url ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Visibilidade">
            <Form.Select
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
              name="status"
            >
              <option>Selecione uma opção</option>
              <option value='active'>🌎 Pública</option>
              <option value='inactive'>🔒 Privada</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <Form.Label>Arquivo de imagem desktop (obrigatório 500x500px)</Form.Label>
          <br />
          <FileUploader
            variant='outline-primary'
            accept='image/*'
            size='sm'
            resolutionHeight={500}
            resolutionWidth={500}
            resolutionType='absolute'
            disabled={formik.isSubmitting || isLoading}
            onReceiveFiles={([{ dataURL }]) => formik.setFieldValue('image', dataURL)}
            onError={error => console.error(error.message)}
          >
            <FontAwesomeIcon icon={faUpload} />
            {' '}
            Enviar arquivo
          </FileUploader>
        </Form.Group>
        <Col md={12}>
          <Button
            variant="link"
            style={{ marginRight: 10 }} onClick={() => navigate('/brands')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
          {' '}
          {customFooterMessage}
        </Col>
      </Row>
    </Form>
  )
}

export default FormBrand
